import { ModelFactory } from '../../$models'
import { FindQuery } from '../../$models/FindQuery'
import { ModelExtensionConfigBase } from '../../../plugins/ModelExtensionModelsLoader/common'
import { FindResultWithMeta } from '../../../types'

type Item = Record<string, any>

const cacheTTLSecondsDefault = 15 * 60
const cacheTTLSecondsDefaultWhenUpdatedAtCheckEnabled = 60 * 60 * 24 * 7 // 7 days

const emptyValues = [null, undefined, '']

/**
 * キャッシュ可能なモデル拡張の設定インターフェース
 *
 * このインターフェースは、モデルのキャッシュ動作を詳細に制御するための設定を定義します。
 * キャッシュの保存場所、更新戦略、依存関係など、様々な側面をカスタマイズできます。
 *
 * ```ts
 * // 基本的な設定例
 * const config: CachedFindableConfig = {
 *   storage: 'hybrid'
 * }
 *
 * // 高度な設定例
 * const advancedConfig: CachedFindableConfig = {
 *   cacheTTLSeconds: 60 * 60 * 24 * 7, // 7日間
 *   storage: 'hybrid',
 *   useUpdatedAtCheck: true,
 *   enableEagerRefresh: true,
 *   updateStrategy: 'hybrid',
 *   fullRefreshIntervalSeconds: 60 * 60 * 24, // 24時間
 *   maxRelationDeepness: 3,
 * }
 * ```
 */
export type CachedFindableConfig = {
  /**
   * キャッシュの有効期限（秒）
   *
   * キャッシュされたデータがどのくらいの期間有効であるかを指定します。
   * この期間を過ぎると、データは再取得されます。
   *
   * 挙動:
   * - 0: キャッシュを完全に無効化します
   * - -1: 無期限にキャッシュを保持します
   * - N > 0: N秒間キャッシュを保持します
   *
   * キャッシュの有効期限が切れた場合:
   * 1. eager refreshが無効の場合: 完全に再取得を行います
   * 2. eager refreshが有効の場合: 古いキャッシュを返しつつ、バックグラウンドで更新を行います
   *
   * @default 900 (15分)
   */
  cacheTTLSeconds?: number

  /**
   * 更新時にキャッシュをパージするかどうか
   *
   * trueの場合、モデルのデータが更新されたとき（save/delete）に
   * 関連するキャッシュを自動的に削除します。
   *
   * 挙動:
   * - true: 以下のイベントでキャッシュが自動的にパージされます
   *   - モデルのレコードが保存されたとき（afterSave）
   *   - モデルのレコードが削除されたとき（afterDelete）
   *   - 依存関係にあるモデルが更新されたとき
   * - false: キャッシュの更新は手動で行う必要があります
   *
   * @default true
   */
  purgeCacheOnUpdate?: boolean

  /**
   * キャッシュストレージの種類
   *
   * データをどこに保存するかを指定します。複数の保存先を組み合わせることで、
   * パフォーマンスと永続性のバランスを取ることができます。
   *
   * 挙動:
   * - 'hybrid': メモリとIndexedDBの両方にキャッシュを保存します (推奨)
   *   - メモリキャッシュの高速性とIndexedDBの永続性を両立します
   *   - メモリキャッシュがクリアされた場合、IndexedDBから復元します
   * - 'memory': メモリ上にのみキャッシュを保存します
   *   - 最も高速ですが、ページリロードでクリアされます
   *   - メモリ使用量に注意が必要です
   * - 'indexedDb': IndexedDBにのみキャッシュを保存します
   *   - ブラウザを閉じても保持されます
   *   - 大容量のデータを保存できます
   *   - メモリキャッシュより若干遅いです
   *
   * @default 'hybrid'
   */
  storage?: 'hybrid' | 'memory' | 'indexedDb'

  /**
   * updatedAt確認戦略の有効化
   *
   * trueの場合、キャッシュを更新する挙動の際に 最新更新日時によるキャッシュチェックを一度実施します。
   * 対象Model の レコードのうち 最も最新の updatedAt を 1件だけ取得し 日時を比較します。
   * 不要な キャッシュ更新のためのデータ取得を回避することができます。
   *
   * 挙動:
   * - true: キャッシュされているデータの最新のupdatedAtと、
   *        DBの最新レコードのupdatedAtを比較します
   * - DBのupdatedAtの方が新しい場合、キャッシュを更新します
   * - この確認は以下のタイミングで行われます:
   *   1. キャッシュヒット時（eager refresh有効時）
   *   2. TTL期限切れ時
   *   3. 明示的な更新チェック時
   *
   * @default true
   */
  useUpdatedAtCheck?: boolean

  /**
   * lastUpdatedAtチェックのインターバル（秒）
   *
   * 指定した秒数の間、lastUpdatedAtのチェックを省略します。
   * 0を指定すると毎回チェック、-1を指定するとチェックしません。
   *
   * @default 60 (1分)
   */
  lastUpdatedAtCheckIntervalSeconds?: number

  /**
   * キャッシュを利用しない条件を定義するための関数
   *
   * ### ユースケース
   * - 特定のクエリ等の条件の場合に キャッシュを利用しないような挙動を定義する際に利用
   *
   * ```ts
   * // 例: ユーザが 特定のロール `運用管理者` に所属している場合には キャッシュを利用しないように 強制
   * if ($core.$embAuth.user.coreRoles.includes('運用管理者')) {
   *   return true // キャッシュを利用しない
   * }
   * return false
   * ```
   *
   * ```ts
   * // 例: query 条件に特定の カラム が 含まれている場合には キャッシュを利用しないように 強制
   * if (findQuery.fields.includes('someColName')) {
   *   return true // キャッシュを利用しない
   * }
   * return false
   * ```
   */
  judgeCacheDisableFunction?: (findQuery: FindQuery) => Promise<boolean>

  /**
   * キャッシュの更新判断関数
   *
   * ### ユースケース
   * - キャッシュの更新をチェックする場合に 追加の条件を定義する際に利用します
   */
  judgeValidCacheAdditionalConfitionFunction?: (
    findQuery: FindQuery,
    cachedValue: CacheValue,
  ) => Promise<boolean>

  /**
   * eager refreshの設定
   *
   * キャッシュヒット時にバックグラウンドで更新チェックを行う機能の設定です。
   * ユーザーへの応答性を保ちながら、データの鮮度も維持できます。
   *
   * 挙動:
   * - true: キャッシュヒット時に以下の処理を行います
   *   1. まず古いキャッシュデータを即座に返却
   *   2. バックグラウンドで更新チェックを実行
   *   3. 更新が必要な場合、次回のアクセスのために新しいデータをキャッシュ
   * - false: 通常のキャッシュ動作（TTLベース）
   *
   * @default true
   */
  enableEagerRefresh?: boolean

  /**
   * Eager Refresh 更新チェックの間隔（秒）
   *
   * 同じキャッシュキーに対する更新チェックの最小間隔を指定します。
   * 短すぎる間隔を指定すると、サーバーに過剰な負荷がかかる可能性があります。
   *
   * 挙動:
   * - 前回の更新チェックから指定時間が経過していない場合、チェックはスキップ
   * - 複数の更新要求は指定間隔でバッチ処理されます
   * - 0を指定すると、毎回更新チェックを実行（非推奨）
   *
   * @default 300 (5分)
   */
  eagerRefreshTTLIntervalSeconds?: number

  /**
   * 更新方法の指定
   *
   * - 'full': 常に全件再取得（デフォルト）
   * - 'delta': 差分更新（追加・変更されたレコードのみ取得）
   * - 'hybrid': ハイブリッド（通常は差分、定期的に全件）
   *
   * @default 'full'
   */
  updateStrategy?: 'full' | 'delta' | 'hybrid'

  /**
   * ハイブリッド更新時の全件更新間隔（秒）
   *
   * 'hybrid'戦略使用時に、何秒ごとに全件更新を行うか指定します。
   *
   * @default 86400 (24時間)
   */
  fullRefreshIntervalSeconds?: number

  /**
   * カスタム更新チェック関数
   *
   * 更新が必要かどうかを判断するカスタムロジックを提供します。
   * trueを返すと更新を実行、falseを返すと更新をスキップします。
   */
  customUpdateCheckFunction?: (modelName: string, lastCheck: number) => Promise<boolean>

  /**
   * バックグラウンド更新の通知コールバック
   *
   * バックグラウンド更新の開始・終了・エラー時に呼び出されます。
   * UIに通知を表示するなどの用途に利用できます。
   */
  updateNotificationCallback?: (
    event: 'start' | 'complete' | 'error',
    modelName: string,
    details?: any,
  ) => void

  /**
   * 単一レコード取得時にキャッシュを利用するかどうか
   *
   * - default では 1件のみ取得の場合には キャッシュは利用されません
   *
   * @default false
   */
  enableCacheWhenSingleRecordFetch?: boolean

  /**
   * リレーションの最大深度
   *
   * 全件キャッシュ使用時に、リレーションをどこまで深く解決するかを指定します。
   * 深い値を設定するとパフォーマンスに影響する可能性があります。
   *
   * @default 2
   */
  maxRelationDeepness?: number
}

export const setupCachedFindableDefaultConfig = (
  givenConfig: CachedFindableConfig,
): CachedFindableConfig => {
  if (givenConfig.useUpdatedAtCheck !== false) {
    givenConfig.useUpdatedAtCheck = true
  }
  const cacheTTLSeconds =
    givenConfig.useUpdatedAtCheck === true
      ? cacheTTLSecondsDefaultWhenUpdatedAtCheckEnabled
      : cacheTTLSecondsDefault
  return {
    cacheTTLSeconds,
    purgeCacheOnUpdate: true,
    storage: 'hybrid',
    useUpdatedAtCheck: true,
    enableEagerRefresh: true,
    eagerRefreshTTLIntervalSeconds: 300,
    maxRelationDeepness: 2,
    lastUpdatedAtCheckIntervalSeconds: 60,
    updateStrategy: 'delta',
    fullRefreshIntervalSeconds: 86400,
    ...givenConfig,
  }
}

/**
 * キャッシュされる値の型定義
 */
export interface CacheValue<T extends Item = Item> {
  modelName: string
  data: FindResultWithMeta<T>
  timestamp: number
  ttl?: number
  filter?: Record<string, any>
  query: FindQuery<T>
  meta?: {
    // cache check 用の 文字列: record.id + record.updatedAt + filterCount
    lastUpdatedAtCheckString?: string
    // dependencies?: {
    //   modelName: string
    //   ids: string[]
    // }[]
    lastRefreshAttempt?: number
  }
}

/**
 * モデルの Find結果をキャッシュする機能を提供する サービスクラス
 */
export class ModelCachedFindableService<T extends Item = Item> {
  public cacheObject: Map<string, CacheValue<T>>
  public indexedDbCache: typeof $core.$indexedDbCache
  private readonly _originalConfig: CachedFindableConfig
  public readonly config: CachedFindableConfig
  public refreshQueue: Map<string, number> = new Map()
  public refreshTimer: NodeJS.Timeout | null = null
  public readonly REFRESH_INTERVAL_SECONDS: number
  public modelName: string
  private tmpCacheFetchPromiseByKey: { [key: string]: Promise<FindResultWithMeta<T>> } =
    {}
  private tmpCacheFetchPromiseResolverByKey: {
    [key: string]: {
      resolve: (value: FindResultWithMeta<T>) => void
      reject: (error: any) => void
    }
  } = {}

  // 更新関連の新しいプロパティ
  private lastUpdatedAtCheckTimestamp = 0
  private lastFullRefreshTimestamp = 0
  private lastUpdatedAtCheckIntervalSeconds = 60

  constructor(config: CachedFindableConfig, modelName: string) {
    this._originalConfig = config
    this.config = setupCachedFindableDefaultConfig(config)
    this.cacheObject = new Map()
    this.indexedDbCache = $core.$indexedDbCache
    this.modelName = modelName
    this.REFRESH_INTERVAL_SECONDS = config.eagerRefreshTTLIntervalSeconds || 300
    if (!this.model.overrides) {
      this.model.overrides = {}
    }
    this.model.overrides.find = this.overrideFindFunction.bind(this)
    this.lastUpdatedAtCheckIntervalSeconds = isNaN(
      config.lastUpdatedAtCheckIntervalSeconds,
    )
      ? 60
      : config.lastUpdatedAtCheckIntervalSeconds
    this.registerCachePurgeHooks()
  }

  /**
   * find メソッドのオーバーライド
   *
   * ## 挙動
   * - query および config に 基づいた cache key を生成
   *   - それ以外の場合: query.limit, offset を考慮したfetch
   *   - query の object key の 順番は補正される
   * - Response promise を生成, map に保持
   *   - Response promise 保持用の key (limit, offset 考慮版) を 生成, 一時的に保持
   *   - 連続リクエストに対して 重複処理を行わないため
   *   - キャッシュの取得 と キャッシュの再取得実施をチェック の 重複を防ぐため
   * - キャッシュの取得
   *   - indexedDb or memory のキャッシュをチェック (strategy による)
   * - キャッシュの再取得実施 要否 をチェック
   * - キャッシュの再取得 を実施
   *   - eager refresh が有効な場合: バックグラウンドで更新チェックを実施 (先に キャッシュを返しておく)
   *   - eager refresh が無効な場合: キャッシュの更新チェックを実施 (blocking)
   * - データを返却
   */
  public async overrideFindFunction(query: FindQuery<T>): Promise<FindResultWithMeta<T>> {
    // キャッシュ利用条件の確認
    if (await this.shouldSkipCache(query)) {
      return this.model.defaultFind(query)
    }
    // クエリの正規化
    const normalizedQuery = this.normalizeQuery(query)

    const cacheKey = this.generateKey({
      modelName: this.modelName,
      query: normalizedQuery,
    })
    try {
      if (typeof this.tmpCacheFetchPromiseByKey[cacheKey]?.then === 'function') {
        const cachedRes = await this.tmpCacheFetchPromiseByKey[cacheKey]
        if (cachedRes) {
          this.cleanupTmpCacheFetchPromise(cacheKey)
          return cachedRes
        }
      }

      this.tmpCacheFetchPromiseByKey[cacheKey] = new Promise(async (resolve, reject) => {
        this.tmpCacheFetchPromiseResolverByKey[cacheKey] = { resolve, reject }
      })

      // 通常のキャッシュ処理
      const result = await this.processWithRegularCache(normalizedQuery)
      this.tmpCacheFetchPromiseResolverByKey[cacheKey]?.resolve?.(result)
      this.cleanupTmpCacheFetchPromise(cacheKey)
      return result
    } catch (error) {
      console.error(
        `[ModelCachedFindableService] (${this.modelName}) Error in overrideFindFunction:`,
        error,
      )
      // エラー時はデフォルトの検索処理にフォールバック
      return this.model.defaultFind(query)
    } finally {
      this.cleanupTmpCacheFetchPromise(cacheKey)
    }
  }

  /**
   * キャッシュをスキップすべきかどうかを判定
   */
  private async shouldSkipCache(query: FindQuery<T> | null = {}): Promise<boolean> {
    // キャッシュが明示的に無効化されている場合
    if (query?.disableCache === true) {
      return true
    }

    // 単一レコード取得時のキャッシュ設定
    const isSingleRecordFetch = this.isSingleRecordFetch(query)
    if (this.config.enableCacheWhenSingleRecordFetch !== true && isSingleRecordFetch) {
      return true
    }

    // カスタム判定関数
    if (typeof this.config.judgeCacheDisableFunction === 'function') {
      return await this.config.judgeCacheDisableFunction(query)
    }

    return false
  }

  /**
   * クエリを正規化
   */
  private normalizeQuery(query: FindQuery<T> | null = {}): FindQuery<T> {
    // JSON.parseとJSON.stringifyでディープコピーを作成（Proxyオブジェクト対策）
    const normalizedQuery = JSON.parse(JSON.stringify(query || {}))

    // 不要なプロパティを削除
    delete normalizedQuery.disableCache
    delete normalizedQuery.virtualModelName

    // ページネーション処理
    if (normalizedQuery.page && normalizedQuery.limit > 1) {
      normalizedQuery.offset = (normalizedQuery.page - 1) * normalizedQuery.limit
    }

    return normalizedQuery
  }

  /**
   * 更新チェックを実行すべきかどうかを判断
   */
  private async shouldCheckForUpdates(): Promise<boolean> {
    // カスタム関数がある場合はそれを使用
    if (typeof this.config.customUpdateCheckFunction === 'function') {
      return await this.config.customUpdateCheckFunction(
        this.modelName,
        this.lastUpdatedAtCheckTimestamp,
      )
    }

    const now = Date.now()
    // インターバルが-1なら常にチェックしない
    if (this.lastUpdatedAtCheckIntervalSeconds < 0) return false

    // インターバルが0なら常にチェック
    if (this.lastUpdatedAtCheckIntervalSeconds === 0) return true

    // インターバル内ならチェックしない
    if (
      now - this.lastUpdatedAtCheckTimestamp <
      this.lastUpdatedAtCheckIntervalSeconds * 1000
    ) {
      return false
    }

    this.lastUpdatedAtCheckTimestamp = now
    return true
  }

  /**
   * 通常のキャッシュ処理
   */
  private async processWithRegularCache(
    query: FindQuery<T>,
  ): Promise<FindResultWithMeta<T>> {
    // キャッシュキーを生成
    const cacheKey = this.generateKey({
      modelName: this.modelName,
      query,
    })

    try {
      const hasMemoryCache = this.cacheObject.has(cacheKey)
      const cachedValue = await this.getCache(cacheKey)
      const isOffline = window.navigator.onLine === false
      // オフラインなら 即時返却
      if (isOffline && cachedValue?.data) {
        return this.modifyReturnValues(cachedValue?.data, query)
      }

      // キャッシュの有効性チェック
      const hasTTLExpired = cachedValue ? this.hasTTLExpired(cachedValue) : true
      const hasCacheAndNoNeedToRefresh = !!cachedValue && !hasTTLExpired

      if (hasMemoryCache && hasCacheAndNoNeedToRefresh) {
        // キャッシュヒット時の処理
        // バックグラウンド更新チェック
        if (this.config.enableEagerRefresh) {
          this.scheduleEagerRefresh(cacheKey, cachedValue, query)
        }

        return this.modifyReturnValues(cachedValue.data, query)
      }

      // キャッシュミスまたはTTL期限切れの場合
      let lastUpdatedAtCheckStringNewFetched = ''

      // updatedAtによるキャッシュ更新チェック
      if (cachedValue && this.shouldUseLastUpdatedAtCheck) {
        // 更新チェックが必要かどうか確認
        if (!hasMemoryCache || (await this.shouldCheckForUpdates())) {
          const { isStilValid, lastUpdatedAtCheckString } =
            await this.checkCacheIsStilValidWithReFetchLatestUpdatedAt(cachedValue)
          lastUpdatedAtCheckStringNewFetched = lastUpdatedAtCheckString

          if (isStilValid) {
            // TTLを更新するために再保存
            this.saveCache(
              cacheKey,
              cachedValue.data,
              query,
              lastUpdatedAtCheckStringNewFetched,
            )
            return this.modifyReturnValues(cachedValue.data, query)
          } else {
            // updatedAtが異なる場合は全キャッシュを無効化
            this.invalidateAllCaches()
          }
        } else {
          // チェック不要の場合でもTTLを更新
          const _lu =
            lastUpdatedAtCheckStringNewFetched ||
            cachedValue?.meta?.lastUpdatedAtCheckString
          this.saveCache(cacheKey, cachedValue.data, query, _lu)
          return this.modifyReturnValues(cachedValue.data, query)
        }
      }

      // データを取得
      const fetched = await this.model.defaultFind(query)
      if (!lastUpdatedAtCheckStringNewFetched) {
        // Cache key を取得
        const checked =
          await this.checkCacheIsStilValidWithReFetchLatestUpdatedAt(cachedValue)
        lastUpdatedAtCheckStringNewFetched = checked.lastUpdatedAtCheckString
      }
      await this.saveCache(cacheKey, fetched, query, lastUpdatedAtCheckStringNewFetched)
      return this.modifyReturnValues(fetched, query)
    } catch (e) {
      console.error(
        `[ModelCachedFindableService] ("modelName: ${this.modelName}") Error in processWithRegularCache:`,
        e,
      )
      throw e
    }
  }

  /**
   * バックグラウンド更新チェックのスケジュール
   */
  private scheduleEagerRefresh(
    cacheKey: string,
    cachedValue: CacheValue<T>,
    query: FindQuery<T>,
  ): void {
    const lastRefreshAttempt = cachedValue.meta?.lastRefreshAttempt || 0
    const refreshInterval = this.REFRESH_INTERVAL_SECONDS * 1000

    if (Date.now() - lastRefreshAttempt > refreshInterval) {
      // メタデータ更新
      if (cachedValue.meta) {
        cachedValue.meta.lastRefreshAttempt = Date.now()
      } else {
        cachedValue.meta = { lastRefreshAttempt: Date.now() }
      }

      // キャッシュ更新
      this.cacheObject.set(cacheKey, cachedValue)

      // バックグラウンドで更新チェック
      setTimeout(async () => {
        try {
          // 更新チェックが必要か確認
          if (await this.shouldCheckForUpdates()) {
            // 更新チェック
            const { isStilValid, lastUpdatedAtCheckString } =
              await this.checkCacheIsStilValidWithReFetchLatestUpdatedAt(cachedValue)

            if (!isStilValid) {
              // データを再取得
              const fetched = await this.model.defaultFind(query)
              await this.saveCache(cacheKey, fetched, query, lastUpdatedAtCheckString)
            }
          }
        } catch (error) {
          console.error(
            `[ModelCachedFindableService] Error in background refresh for cache key ${cacheKey}:`,
            error,
          )
        }
      }, 100)
    }
  }

  get shouldUseLastUpdatedAtCheck(): boolean {
    return this.config.useUpdatedAtCheck !== false
  }

  private isSingleRecordFetch(query: FindQuery<T>): boolean {
    if (!query) return false
    if (query.limit == 1) {
      return true
    }
    if (query.filter?.[this.model.primaryKeyColName]?._eq) {
      return true
    }
    return false
  }

  private cleanupTmpCacheFetchPromise(cacheKey: string) {
    setTimeout(() => {
      delete this.tmpCacheFetchPromiseByKey[cacheKey]
      delete this.tmpCacheFetchPromiseResolverByKey[cacheKey]
    }, 100)
  }

  private modifyReturnValues(fetched: FindResultWithMeta<T>, query: FindQuery<T>) {
    return fetched
  }

  get model() {
    return $core.$models[this.modelName]
  }

  private registerCachePurgeHooks() {
    // イベントハンドラの設定
    if (this.config.purgeCacheOnUpdate !== false) {
      const targetHookNames = [
        `${this.modelName}.afterSave`,
        `${this.modelName}.afterDelete`,
      ]
      targetHookNames.forEach((hookName) => {
        $core.$appHook.on(hookName, async (record) => {
          try {
            await this.invalidateAllCaches()
          } catch (error) {
            console.error(
              `Error in cache invalidation after ${hookName} for model ${this.modelName}:`,
              error,
              {
                record,
                modelName: this.modelName,
              },
            )
            // エラーが発生した場合でもキャッシュを無効化する試み
            try {
              await this.invalidateAllCaches()
            } catch (retryError) {
              console.error(
                `Retry failed for cache invalidation after ${hookName} for model ${this.modelName}:`,
                retryError,
              )
            }
          }
        })
      })
    }
  }

  /**
   * キャッシュキーを生成
   */
  generateKey(params: { modelName: string; query: FindQuery<T> }): string {
    const { modelName, query } = params

    const normalizedQuery = this.sortObjectDeep(query || {})
    const normalizedQueryHashed = $core.$utils.md5(JSON.stringify(normalizedQuery))

    return JSON.stringify({
      model: modelName,
      query: normalizedQueryHashed,
    })
  }

  /**
   * キャッシュからデータを取得
   */
  private async getCache(key: string): Promise<CacheValue<T> | null> {
    try {
      // メモリキャッシュをチェック
      if (['hybrid', 'memory'].includes(this.config.storage)) {
        const memoryValue = this.cacheObject.get(key)
        // memory の場合は ここで 返却する
        if (memoryValue || this.config.storage === 'memory') {
          return memoryValue
        }
      }

      // IndexedDBをチェック
      if (['hybrid', 'indexedDb'].includes(this.config.storage)) {
        const dbValue = await this.indexedDbCache.get(this.getIndexedDbKey(key))
        return dbValue
      }
    } catch (error) {
      console.error('Error retrieving from cache:', error)
    }

    return null
  }

  /**
   * キャッシュにデータを保存
   */
  async saveCache(
    key: string,
    value: FindResultWithMeta<T>,
    query: FindQuery<T>,
    lastUpdatedAtCheckStringNewFetched: string,
  ): Promise<void> {
    try {
      const cacheTTLSeconds = this.config.cacheTTLSeconds
      const cacheValue: CacheValue<T> = {
        modelName: this.modelName,
        data: value,
        timestamp: Date.now(),
        ttl: cacheTTLSeconds * 1000,
        query,
        meta: {
          lastUpdatedAtCheckString: lastUpdatedAtCheckStringNewFetched,
          // dependencies: this.extractDependencies(value),
        },
      }

      if (['memory', 'hybrid'].includes(this.config.storage)) {
        this.cacheObject.set(key, cacheValue)
      }

      if (['indexedDb', 'hybrid'].includes(this.config.storage)) {
        await this.indexedDbCache.set(this.getIndexedDbKey(key), cacheValue, -1)
      }
    } catch (error) {
      console.error('Error saving to cache:', error)
    }
  }

  /**
   * モデルの全キャッシュを無効化
   */
  async invalidateAllCaches(): Promise<void> {
    try {
      // 1. メモリキャッシュのクリア
      if (['hybrid', 'memory'].includes(this.config.storage)) {
        this.cacheObject.clear()
      }

      // 2. IndexedDBのキャッシュ無効化（プレフィックスベース）
      if (['hybrid', 'indexedDb'].includes(this.config.storage)) {
        const keyPrefix = `modelCache_${this.modelName}_`
        const deletedCount = await this.indexedDbCache.deleteByPrefix(keyPrefix)
        console.debug(
          `[ModelCachedFindableService] Invalidated ${deletedCount} cache entries for model: ${this.modelName}`,
        )
      }
    } catch (error) {
      console.error(
        `[ModelCachedFindableService] Error in invalidateAllCaches for model ${this.modelName}:`,
        error,
      )
    }
  }

  // Private utility methods
  private hasTTLExpired(value: CacheValue<T>): boolean {
    if (!value.ttl) return false
    return Date.now() - value.timestamp > value.ttl
  }

  // private pickLatestUpdatedAtCheckStringFromRecords(data: T[]): string | undefined {
  //   return data.reduce(
  //     (latest, item) => {
  //       if (!item.updatedAt) return latest
  //       return !latest || item.updatedAt > latest ? item.updatedAt : latest
  //     },
  //     undefined as string | undefined,
  //   )
  // }

  /**
   * テーブル全体の最新updatedAtを取得
   *
   * ## 挙動
   * - レコードが存在する場合: "updatedAt|primaryKey|filter_count" の形式で返す
   * - レコードが0件の場合: "EMPTY_TABLE|0" を返す（0件の状態もキャッシュ可能にするため）
   * - エラーが発生した場合: null を返す
   */
  private async getLatestTableUpdatedAt(): Promise<string | null> {
    try {
      const result = await this.model.defaultFind({
        limit: 1,
        sort: ['-updatedAt'],
        fields: ['updatedAt', this.model.primaryKeyColName],
        meta: ['filter_count'],
      })

      // レコードが存在しない場合（0件）は特別な文字列を返す
      // これにより0件の状態もキャッシュ可能になる
      const filterCount = result.meta?.filter_count || 0
      if (!result.data?.[0]?.updatedAt || filterCount === 0) {
        return 'EMPTY_TABLE|0'
      }

      return [
        result.data[0].updatedAt,
        result.data[0][this.model.primaryKeyColName],
        filterCount,
      ].join('|')
    } catch (error) {
      console.error('Error getting latest table updatedAt:', error)
      return null
    }
  }

  /**
   * キャッシュの更新チェック
   *
   * ## 挙動
   * - テーブルの最新updatedAtを取得し、キャッシュされている値と比較
   * - 0件の場合も "EMPTY_TABLE|0" として比較される
   * - 一致すればキャッシュは有効、不一致なら無効と判断
   */
  private async checkCacheIsStilValidWithReFetchLatestUpdatedAt(
    cached: CacheValue<T>,
  ): Promise<{
    isStilValid: boolean
    lastUpdatedAtCheckString: string
  }> {
    const latestTableUpdatedAt = await this.getLatestTableUpdatedAt()

    // エラー時のみfalseを返す（nullまたは空文字列の場合）
    // 0件の場合は 'EMPTY_TABLE|0' が返されるため、このチェックには引っかからない
    if (!latestTableUpdatedAt) {
      return {
        isStilValid: false,
        lastUpdatedAtCheckString: '',
      }
    }

    const isCacheValidBasedOnUpdatedAt =
      cached?.meta?.lastUpdatedAtCheckString === latestTableUpdatedAt
    return {
      isStilValid: isCacheValidBasedOnUpdatedAt,
      lastUpdatedAtCheckString: latestTableUpdatedAt,
    }
  }

  /**
   * 正しく Cache hit させるために Filter condition などを normalize する
   * @param obj
   * @private
   */
  private sortObjectDeep(obj: Record<string, any>): Record<string, any> {
    // オブジェクトでない場合は そのまま値として返却
    if (typeof obj !== 'object' || obj === null) return obj

    if (Array.isArray(obj)) {
      return obj.map((item) => this.sortObjectDeep(item)).sort()
    }

    const sorted: Record<string, any> = {}
    Object.keys(obj)
      .sort()
      .forEach((key) => {
        // empty values は除外
        if (emptyValues.includes(obj[key])) {
          return
        }
        // empty object は除外
        if (typeof obj[key] === 'object' && Object.keys(obj[key]).length === 0) {
          return
        }
        // empty array は除外
        if (Array.isArray(obj[key]) && obj[key].length === 0) {
          return
        }
        sorted[key] = this.sortObjectDeep(obj[key])
      })

    return sorted
  }

  private getIndexedDbKey(key: string): string {
    return `modelCache_${this.modelName}_${key}`
  }
}

/**
 * # Front 検索Cache Model Extension
 * - Model単位で Front (Browser) 内での 検索・fetch のキャッシュを有効化します
 *
 * ## 有効化方法
 * - Model定義の extensions に以下の設定を追加
 * ```ts
 * extensions: [
 *   {
 *     extensionType: 'cachedFindable',
 *     enabled: true,
 *     cacheTTLSeconds: 60 * 5, // キャッシュの有効期限 (秒)
 *     purgeCacheOnUpdate: true, // 更新時にキャッシュをパージするかどうか
 *     storage: 'hybrid', // 'memory' | 'indexedDb' | 'hybrid'
 *     updateStrategy: {
 *       useUpdatedAtCheck: true, // updatedAtによる更新チェックを有効化
 *       eagerRefresh: {
 *         enabled: true, // eager refreshを有効化
 *         interval: 1000, // リフレッシュ間隔（ミリ秒）
 *       },
 *       batchUpdate: {
 *         enabled: true,
 *         maxQueueSize: 20,
 *         maxWaitTime: 1000,
 *       }
 *     }
 *   }
 * ]
 * ```
 *
 * ## 利用シーン
 * - 頻繁に検索・fetch を実施するモデルに対してキャッシュを利用するように設定可能
 * - オフライン対応が必要な場合はIndexedDBストレージを利用可能
 *
 * ## 挙動
 * - キャッシュストレージは memory, indexedDb, hybrid から選択可能
 * - クエリ毎に、キャッシュが存在する場合は、キャッシュを返却します
 *   - キャッシュキーは filter, limit, fields, offset, aggregate その他, すべてのクエリパラメータを含めたものとなります
 * - eager refresh が有効な場合、キャッシュ返却後にバックグラウンドで更新チェックを実行
 * - キャッシュがパージされるタイミングは 設定変更が可能です
 */
export const installModelExtensionCachedFindable = (
  model: ModelFactory,
  config: CachedFindableConfig,
) => {
  // キャッシュ戦略の初期化
  new ModelCachedFindableService(config, model.tableName)
  // console.log(`[ModelCachedFindableService] Cache enabled for model: ${model.tableName}`)
}

export const extensionTypeOfModelExtensionCachedFindable = 'cachedFindable'

export type ModelExtensionCachedFindableConfig = ModelExtensionConfigBase &
  CachedFindableConfig & {
    extensionType: typeof extensionTypeOfModelExtensionCachedFindable
  }
